<template>
  <e-form v-model="isValid" ref="formRef" @submit="onSubmit">
    <div class="h3 mb-4">Выбор отеля для подключения</div>
    <div class="paragraph-s_h mb-10 text-secondary">
      Укажите ID и наименование отеля в
      {{ channelManagerCode === ChannelManagerCode.TravelLine ? 'TravelLine' : 'Exely' }}.
    </div>
    <v-img
      v-if="$vuetify.display.mdAndUp"
      :src="channelManagerCode === ChannelManagerCode.TravelLine ? TravelLineTutorial : ExelyTutorial"
    />
    <div class="mt-10" />
    <e-alert v-if="isLegalDataInvalid" variant="error" class="mb-4">
      <div>
        <div class="paragraph-m"><b>Некорректный ID или наименование</b></div>
        <div class="paragraph-s_h">
          Пара ID + наименование не найдена, возможно вы некорректно указали одно из значений. Если ошибка продолжает
          появляться - обратитесь в нашу
          <router-link :to="{ name: RouteNames.Support }" class="link">Поддержку</router-link>
        </div>
      </div>
    </e-alert>
    <e-combobox
      v-model="data.id"
      :items="hotelSuggestions"
      :error-style="isLegalDataInvalid"
      :rules="[required(), maxLength(9)]"
      :disabled="!!integration"
      item-title="id"
      item-value="id"
      return-object
      :maska="commonNumberMaskForValidation"
      label="ID отеля в системе"
      class="mb-4"
      @update:modelValue="onHotelSelect"
    >
      <template #item="{ item }">
        <div class="input-text">{{ item.raw.id }} / {{ item.raw.name }}</div>
        <div v-if="item.raw.uncompletedInteractionId" class="input-text">
          <c-m-status-string status="inProgress" />
        </div>
      </template>
    </e-combobox>
    <e-input
      v-model="data.name"
      :error-style="isLegalDataInvalid"
      :rules="[required()]"
      :disabled="!!integration"
      label="Наименование отеля в системе"
    />

    <teleport v-if="isMounted" defer :to="`#${travelLineIntegrationSubmitButtonTeleport}`">
      <e-btn append-icon-src="arrowRight" :loading="isLoading" variant="primary" @click="submit"> Далее </e-btn>
    </teleport>
  </e-form>
</template>

<script setup lang="ts">
import { useMounted } from '@vueuse/core';
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { EComboboxModelType } from '@/core/types/components/components';
import type { Nullable } from '@/core/types/gengeric';
import type { TLIntegrationHotelData } from '@/parts/tariffs/types/travel-line';
import ExelyTutorial from '@/assets/img/pages/tariffs/channel-manager/tutorual-exely.webp';
import TravelLineTutorial from '@/assets/img/pages/tariffs/channel-manager/tutorual.webp';
import { EAlert, EBtn, ECombobox, EForm, EInput } from '@/core/components/ui';
import { commonNumberMaskForValidation } from '@/core/consts/masks';
import { travelLineIntegrationSubmitButtonTeleport } from '@/core/consts/teleport';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { isString } from '@/core/utils/type-guards';
import { maxLength, required } from '@/core/utils/validation';
import { ChannelManagerCode } from '@/graphql/generated';
import { errorTypes } from '@/parts/errors/utils/error-types';
import CMStatusString from '@/parts/tariffs/components/CM/CMStatusString.vue';
import { useTravelLineStore } from '@/parts/tariffs/store/travelLine';

const $router = useRouter();

const isMounted = useMounted();

const formRef = ref();

const {
  getHotelSuggestions,
  hotelSuggestions,
  startIntegration,
  checkUncompleteIntegrationInfo,
  getTravelLineIntegration,
  setActiveStep,
  checkActiveStep,
  activeStep,
  channelManagerCode,
  integration,
  externalIntegration,
} = useTravelLineStore();

const data = reactive<Nullable<TLIntegrationHotelData>>({
  id: externalIntegration?.value?.externalIntegration?.externalHotel?.hotelId ?? null,
  name: externalIntegration?.value?.externalIntegration?.externalHotel?.hotelName ?? null,
  uncompletedInteractionId: null,
});

const isValid = ref(false);

const isLegalDataInvalid = ref(false);
const isLoading = ref(false);

const onHotelSelect = (value: EComboboxModelType<TLIntegrationHotelData>) => {
  data.uncompletedInteractionId = null;

  if (value && !isString(value)) {
    data.id = String(value.id);
    data.name = value.name;
    data.uncompletedInteractionId = value.uncompletedInteractionId;
  }
};

const onSubmit = async () => {
  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  let result = null;

  if (!data.uncompletedInteractionId) {
    result = await startIntegration(+data.id!, data.name!);

    if (result?.errors) {
      if (
        result.errors[0].__typename === errorTypes.TravelLineHotelNotFoundError ||
        result.errors[0].__typename === errorTypes.TravelLineExternalIntegrationIssueError
      ) {
        isLegalDataInvalid.value = result.errors[0].__typename === errorTypes.TravelLineHotelNotFoundError;

        await checkUncompleteIntegrationInfo(+data.id!, data.name!);

        isLoading.value = false;
        return;
      }
    }

    if (result?.data) {
      data.uncompletedInteractionId = result.data.travelLineStartIntegration.InteractionId;
    }
  }

  if (data.uncompletedInteractionId) {
    await $router.replace({
      query: {
        interactionId: data.uncompletedInteractionId,
        manager: channelManagerCode.value,
      },
    });
  }

  await getTravelLineIntegration();

  checkActiveStep();

  if (activeStep.value === 3) {
    setActiveStep(2);
  }

  isLoading.value = false;
};

const submit = () => {
  formRef.value?.validate();
  onSubmit();
};

await getHotelSuggestions();
</script>

<style scoped lang="scss"></style>
